import React from 'react';
import { Book } from '@lifeomic/chromicons';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { PresentationAction } from '../../../Presentation';
import bloodPressureChart from '../../../../static/images/executive-summary-reference-images/blood-pressure-chart.png';
import cardiometabolicDiagram from '../../../../static/images/executive-summary-reference-images/cardiometabolic-diagram.png';
import distributionOfTotalPlaqueByAgeFemale from '../../../../static/images/executive-summary-reference-images/distribution-of-total-plaque-by-age-female.png';
import distributionOfTotalPlaqueByAgeMale from '../../../../static/images/executive-summary-reference-images/distribution-of-total-plaque-by-age-male.png';
import femaleSexHormonesAndMenstrualCycle from '../../../../static/images/executive-summary-reference-images/female-sex-hormones-and-menstrual-cycle.png';
import galleriSensitivityValues from '../../../../static/images/executive-summary-reference-images/galleri-sensitivity-values.png';
import gripStrengthFemale from '../../../../static/images/executive-summary-reference-images/grip-strength-female.png';
import gripStrengthMale from '../../../../static/images/executive-summary-reference-images/grip-strength-male.png';
import heartAnatomy from '../../../../static/images/executive-summary-reference-images/heart-anatomy.png';
import heartBloodVesselAnatomy from '../../../../static/images/executive-summary-reference-images/heart-blood-vessel-anatomy.png';
import lpPla2Physiology from '../../../../static/images/executive-summary-reference-images/lp-pla2-physiology.png';
import mpoAndTheEvolutionOfCvd from '../../../../static/images/executive-summary-reference-images/mpo-and-the-evolution-of-cvd.png';
import neurocognitiveTestsAndDomainScoringProcess from '../../../../static/images/executive-summary-reference-images/neurocognitive-tests-and-domain-scoring-process.png';
import perivascularSpaces from '../../../../static/images/executive-summary-reference-images/perivascular-spaces.png';
import plaqueNomogramFemale from '../../../../static/images/executive-summary-reference-images/plaque-nomogram-female.png';
import plaqueNomogramMale from '../../../../static/images/executive-summary-reference-images/plaque-nomogram-male.png';
import plaqueStages from '../../../../static/images/executive-summary-reference-images/plaque-stages.png';
import populationPercentileCutPoints from '../../../../static/images/executive-summary-reference-images/population-percentile-cut-points.png';
import vascularInflammatoryAndOxidationMarkers from '../../../../static/images/executive-summary-reference-images/vascular-inflammatory-and-oxidation-markers.png';
import visceralFatMortality from '../../../../static/images/executive-summary-reference-images/visceral-fat-mortality.png';
import bloodAndItsComponents from '../../../../static/images/executive-summary-reference-images/blood-and-its-components.png';
import plasmaProteins from '../../../../static/images/executive-summary-reference-images/plasma-proteins.png';
import ironStorageAndSerum from '../../../../static/images/executive-summary-reference-images/iron-storage-and-serum.png';
import thyroidFactors from '../../../../static/images/executive-summary-reference-images/thyroid-factors.png';
import krebsCycle from '../../../../static/images/executive-summary-reference-images/krebs-cycle.png';

type ReferenceImage = {
  displayName: string;
  url: string;
};

type ImpressionType = string;

const referenceImages: Record<ImpressionType, ReferenceImage[]> = {
  heart: [
    {
      displayName: 'Blood Pressure Chart',
      url: bloodPressureChart,
    },
    {
      displayName: 'Heart Chamber and Valve Anatomy',
      url: heartAnatomy,
    },
    {
      displayName: 'Heart Blood Vessel Anatomy',
      url: heartBloodVesselAnatomy,
    },
    {
      displayName: 'Plaque Nomogram (Female)',
      url: plaqueNomogramFemale,
    },
    {
      displayName: 'Plaque Nomogram (Male)',
      url: plaqueNomogramMale,
    },
    {
      displayName: 'Population Percentile Cut Points',
      url: populationPercentileCutPoints,
    },
    {
      displayName: 'Distribution of Total Plaque by Age (Male)',
      url: distributionOfTotalPlaqueByAgeMale,
    },
    {
      displayName: 'Distribution of Total Plaque by Age (Female)',
      url: distributionOfTotalPlaqueByAgeFemale,
    },
    {
      displayName: 'Plaque Stages',
      url: plaqueStages,
    },
    {
      displayName: 'Lp-PLA2 Physiology',
      url: lpPla2Physiology,
    },
    {
      displayName: 'MPO and the Evolution of CVD',
      url: mpoAndTheEvolutionOfCvd,
    },
    {
      displayName: 'Vascular Inflammatory and Oxidation Markers',
      url: vascularInflammatoryAndOxidationMarkers,
    },
    {
      displayName: 'Cardiometabolic Diagram',
      url: cardiometabolicDiagram,
    },
  ],
  cancer: [
    {
      displayName: 'Galleri Sensitivity Values',
      url: galleriSensitivityValues,
    },
  ],
  brain: [
    {
      displayName: 'Perivascular Spaces',
      url: perivascularSpaces,
    },
    {
      displayName: 'Neurocognitive Tests and Domain Scoring Process',
      url: neurocognitiveTestsAndDomainScoringProcess,
    },
  ],
  musculoskeletal: [
    {
      displayName: 'Grip Strength (Male)',
      url: gripStrengthMale,
    },
    {
      displayName: 'Grip Strength (Female)',
      url: gripStrengthFemale,
    },
    {
      displayName: 'Visceral Fat Mortality',
      url: visceralFatMortality,
    },
  ],
  metabolic: [
    {
      displayName: 'Cardiometabolic Diagram',
      url: cardiometabolicDiagram,
    },
    {
      displayName: 'Thyroid Factors',
      url: thyroidFactors,
    },
    {
      displayName: 'Krebs Cycle',
      url: krebsCycle,
    },
  ],
  labResults: [
    {
      displayName: 'Female Sex Hormones & Menstrual Cycle',
      url: femaleSexHormonesAndMenstrualCycle,
    },
    {
      displayName: 'Plasma Proteins',
      url: plasmaProteins,
    },
    {
      displayName: 'Blood and its Components',
      url: bloodAndItsComponents,
    },
    {
      displayName: 'Iron Storage and Serum',
      url: ironStorageAndSerum,
    },
  ],
};

// Sort the reference images by displayName
export const REFERENCE_IMAGES: Record<ImpressionType, ReferenceImage[]> =
  Object.fromEntries(
    Object.entries(referenceImages).map(([key, value]) => [
      key,
      value.sort((a, b) => a.displayName.localeCompare(b.displayName)),
    ]),
  );

const useStyles = makeStyles(theme => ({
  referenceImageItem: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    height: 'fit-content',
    width: '100%',
    '&:hover, &:focus, &:focus-visible': {
      backgroundColor: 'transparent',
    },
  },
  modal: {
    width: 'auto',
    maxWidth: '90vw',
  },
  modalContent: {
    padding: theme.spacing(2),
    maxWidth: 'none',
    height: 'auto',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  image: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 'calc(80vh - 80px)',
    width: 'auto',
    height: 'auto',
    margin: '0 auto',
  },
}));

type ReferenceImageMenuProps = {
  className?: string | boolean;
  impressionType?: ImpressionType;
};

export const ReferenceImageMenu = ({
  impressionType,
}: ReferenceImageMenuProps) => {
  const classes = useStyles();
  const [selectedReferenceImage, setSelectedReferenceImage] =
    React.useState<ReferenceImage | null>(null);
  const isModalOpen = selectedReferenceImage !== null;
  const hasReferenceImages =
    impressionType &&
    impressionType in REFERENCE_IMAGES &&
    REFERENCE_IMAGES[impressionType].length > 0;

  return (
    <>
      <Box>
        {hasReferenceImages && (
          <Menu
            aria-label="References menu"
            placement="right-start"
            gutter={12}
            anchorElement={
              <PresentationAction title="References" icon={Book} />
            }
            items={REFERENCE_IMAGES[impressionType].map(referenceImage => (
              <MenuItem
                onClick={() => setSelectedReferenceImage(referenceImage)}
                key={referenceImage.url}
              >
                <Box className={classes.referenceImageItem}>
                  <Text size="caption">{referenceImage.displayName}</Text>
                </Box>
              </MenuItem>
            ))}
          />
        )}
      </Box>
      <Modal
        title={selectedReferenceImage?.displayName}
        isOpen={isModalOpen}
        onDismiss={() => setSelectedReferenceImage(null)}
        contentClassName={classes.modalContent}
        className={classes.modal}
      >
        {selectedReferenceImage && (
          <Box>
            <img
              src={selectedReferenceImage.url}
              alt={selectedReferenceImage.displayName}
              className={classes.image}
            />
          </Box>
        )}
      </Modal>
    </>
  );
};
